export const blockPatterns: RegExp[] = [
  // 1. Phone Numbers - More precise detection (reduced false positives)
  // Only match clear phone number patterns, not building numbers or postal codes
  /(?:\+91[\s\-\.]?)?[6-9]\d{9}(?!\d)/g, // Standard 10-digit mobile
  /(?:\+91[\s\-\.]?)?[6-9][\s\-\.]{1,2}\d{4}[\s\-\.]{1,2}\d{4}/g, // Formatted mobile
  /(?:\+91[\s\-\.]?)?[6-9]\d[\s\-\.]\d{3}[\s\-\.]\d{3}[\s\-\.]\d{3}/g, // Specific formatting
  
  // 2. WhatsApp references - Keep strict
  /whatsapp|what\s*app|wa\.me/gi,
  /wa[\s\-\.]*me[\s\-\.]*\/[\s\-\.]*[0-9]{9,12}/gi,
  
  // 3. Email Addresses - Keep strict but more precise
  /[a-z0-9._%+-]+\s*(@|\[at\]|\(at\)|\sat\s)\s*[a-z0-9.-]+\s*(\.|dot|\[dot\]|\(dot\))\s*[a-z]{2,}/gi,
  
  // 4. Social Media Handles - Keep strict
  /instagram\s*[@:=\-]*\s*[@a-z0-9_.]+/gi,
  /insta\s*[@:=\-]*\s*[@a-z0-9_.]+/gi,
  /ig\s*[@:=\-]*\s*[@a-z0-9_.]+/gi,
  /telegram\s*[@:=\-]*\s*[@a-z0-9_.]+/gi,
  /tg\s*[@:=\-]*\s*[@a-z0-9_.]+/gi,
  
  // 5. UPI/Payment Handles - Keep strict
  /[a-z0-9.-]{3,}@(upi|paytm|phonepe|gpay|googlepay|okaxis|okhsbc|okicici|oksbi)/gi,
  
  // 6. Direct contact sharing prompts - More specific
  /(contact|call|reach|ping|text|message|dm)\s*(me)?\s*(on|at|via)?\s*[:=\-]*\s*(\+91)?\s*[6-9]\d{9}/gi,
  
  // 7. Remove the overly broad number sequence pattern that was causing issues
  // Only flag if it's clearly a phone number pattern
  /(?:call|contact|phone|mobile|number)\s*[:=\-]*\s*(?:\+91\s*)?[6-9][\d\s\-\.]{8,11}\d/gi,
];

// Keep suspicious patterns but make them more specific
export const suspiciousPatterns: RegExp[] = [
  /outside\s+platform/gi,
  /personal\s+chat/gi,
  /direct\s+message/gi,
  /private\s+chat/gi,
  /off\s+site/gi,
  /share\s+contact/gi,
  /my\s+details/gi,
];


// Enhanced blocking function for general messages
export const isBlockedMessage = (message: string): { 
  isBlocked: boolean; 
  reason: string; 
  severity: 'warning' | 'violation' 
} => {
  const originalMessage = message;
  
  // Check primary blocking patterns
  for (const pattern of blockPatterns) {
    if (pattern.test(originalMessage)) {
      return {
        isBlocked: true,
        reason: 'Contact information sharing detected',
        severity: 'violation'
      };
    }
  }
  
  // Check suspicious patterns
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(originalMessage)) {
      return {
        isBlocked: true,
        reason: 'Suspicious content detected',
        severity: 'warning'
      };
    }
  }
  
  return {
    isBlocked: false,
    reason: '',
    severity: 'warning'
  };
};

// IMPROVED: Much more liberal delivery address validation
export const isDeliveryDetailsBlocked = (text: string): { 
  isBlocked: boolean; 
  reason: string; 
  severity: 'warning' | 'violation' 
} => {
  if (!text || text.trim().length === 0) {
    return {
      isBlocked: false,
      reason: '',
      severity: 'warning'
    };
  }

  const originalText = text.trim();
  
  // 1. STRICT: Check for clear mobile numbers only
  // 10-digit mobile starting with 6-9
  const mobilePattern = /(?:\+91[\s\-\.]?)?[6-9]\d{9}(?!\d)/g;
  if (mobilePattern.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'Mobile numbers are not allowed in address fields',
      severity: 'violation'
    };
  }
  
  // Check for formatted mobile numbers
  const formattedMobilePattern = /(?:\+91[\s\-\.]?)?[6-9][\s\-\.]{1,2}\d{4}[\s\-\.]{1,2}\d{4}/g;
  if (formattedMobilePattern.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'Mobile numbers are not allowed in address fields',
      severity: 'violation'
    };
  }
  
  // 2. STRICT: Check for email addresses
  const emailPattern = /[a-z0-9._%+-]+\s*(@|\[at\]|\(at\))\s*[a-z0-9.-]+\s*(\.|dot)\s*[a-z]{2,}/gi;
  if (emailPattern.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'Email addresses are not allowed in address fields',
      severity: 'violation'
    };
  }
  
  // 3. STRICT: Check for WhatsApp references
  if (/whatsapp|what\s*app|wa\.me/gi.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'WhatsApp references are not allowed in address fields',
      severity: 'violation'
    };
  }
  
  // 4. STRICT: Check for social media handles
  const socialMediaPattern = /(instagram|insta|ig|telegram|tg)\s*[@:=\-]*\s*[@a-z0-9_.]+/gi;
  if (socialMediaPattern.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'Social media handles are not allowed in address fields',
      severity: 'violation'
    };
  }
  
  // 5. STRICT: Check for UPI handles
  const upiPattern = /[a-z0-9.-]{3,}@(upi|paytm|phonepe|gpay|googlepay|okaxis|okhsbc|okicici|oksbi)/gi;
  if (upiPattern.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'Payment handles are not allowed in address fields',
      severity: 'violation'
    };
  }
  
  // 6. LIBERAL: Only flag if there are explicit contact sharing terms with numbers
  const contactSharingPattern = /(contact|call|reach|ping|text|message|dm)\s*(me)?\s*(on|at|via)?\s*[:=\-]*\s*(\+91\s*)?[6-9]\d{9}/gi;
  if (contactSharingPattern.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'Contact sharing prompts are not allowed in address fields',
      severity: 'violation'
    };
  }
  
  // 7. REMOVED: The fragmented phone number detection that was causing false positives
  // Building numbers like "310-B" or postal codes should be allowed
  
  // 8. LIBERAL: Only flag very specific suspicious patterns
  const liberalSuspiciousPatterns = [
    /outside\s+platform/gi,
    /personal\s+chat/gi,
    /share\s+contact/gi
  ];
  
  for (const pattern of liberalSuspiciousPatterns) {
    if (pattern.test(originalText)) {
      return {
        isBlocked: true,
        reason: 'Suspicious content detected in address field',
        severity: 'warning'
      };
    }
  }
  
  return {
    isBlocked: false,
    reason: '',
    severity: 'warning'
  };
};

// User violation tracking (unchanged)
interface UserViolation {
  userId: string;
  violations: number;
  warnings: number;
  lastViolation: Date;
  isFlagged: boolean;
  isBanned: boolean;
}

class ViolationTracker {
  private violations: Map<string, UserViolation> = new Map();
  private readonly MAX_WARNINGS = 3;
  private readonly MAX_VIOLATIONS = 3;
  
  recordViolation(userId: string, severity: 'warning' | 'violation'): UserViolation {
    const existing = this.violations.get(userId) || {
      userId,
      violations: 0,
      warnings: 0,
      lastViolation: new Date(),
      isFlagged: false,
      isBanned: false
    };
    
    if (severity === 'violation') {
      existing.violations++;
    } else {
      existing.warnings++;
    }
    
    existing.lastViolation = new Date();
    existing.isFlagged = existing.warnings >= this.MAX_WARNINGS || existing.violations >= 2;
    existing.isBanned = existing.violations >= this.MAX_VIOLATIONS;
    
    this.violations.set(userId, existing);
    return existing;
  }
  
  getUserStatus(userId: string): UserViolation | null {
    return this.violations.get(userId) || null;
  }
  
  isUserBanned(userId: string): boolean {
    const user = this.violations.get(userId);
    return user?.isBanned || false;
  }
  
  isUserFlagged(userId: string): boolean {
    const user = this.violations.get(userId);
    return user?.isFlagged || false;
  }
}

export const violationTracker = new ViolationTracker();